import React from 'react';
import {
    Input,
    InputNumber,
    Select,
    DatePicker,
    Switch,
    Radio,
    Slider,
    TimePicker,
} from 'antd';
import style from './style.module.scss';

const { Option } = Select;
const { TextArea } = Input;
const { RangePicker } = DatePicker;

export interface InputVariant {
    label: string;
    component: React.ReactNode;
}

export interface InputType {
    label: string;
    variants: InputVariant[];
}

// =============================================================================
// Individual Input Type Creators
// =============================================================================

export function createInputVariants(): InputType {
    return {
        label: 'Input',
        variants: [
            {
                label: 'Standard',
                component: <Input placeholder="Enter text..." />,
            },
            {
                label: 'With Prefix',
                component: <Input prefix="$" placeholder="0.00" />,
            },
            {
                label: 'With Addon After',
                component: (
                    <Input addonAfter="h" placeholder="0" type="number" />
                ),
            },
            {
                label: 'Disabled',
                component: <Input placeholder="Disabled input" disabled />,
            },
        ],
    };
}

export function createInputNumberVariants(): InputType {
    return {
        label: 'InputNumber',
        variants: [
            {
                label: 'Standard',
                component: (
                    <InputNumber
                        min={0}
                        placeholder="0"
                        style={{ width: '100%' }}
                    />
                ),
            },
            {
                label: 'Disabled',
                component: (
                    <InputNumber
                        min={0}
                        placeholder="0"
                        style={{ width: '100%' }}
                        disabled
                    />
                ),
            },
        ],
    };
}

export function createTextAreaVariants(): InputType {
    return {
        label: 'TextArea',
        variants: [
            {
                label: 'Standard',
                component: (
                    <TextArea rows={4} placeholder="Enter multiline text..." />
                ),
            },
            {
                label: 'Disabled',
                component: (
                    <TextArea
                        rows={4}
                        placeholder="Enter multiline text..."
                        disabled
                    />
                ),
            },
        ],
    };
}

export function createSelectVariants(): InputType {
    return {
        label: 'Select',
        variants: [
            {
                label: 'Standard',
                component: (
                    <Select
                        placeholder="Select an option"
                        style={{ width: '100%' }}
                    >
                        <Option value="option1">Option 1</Option>
                        <Option value="option2">Option 2</Option>
                        <Option value="option3">Option 3</Option>
                    </Select>
                ),
            },
            {
                label: 'With Search',
                component: (
                    <Select
                        showSearch
                        placeholder="Search and select..."
                        style={{ width: '100%' }}
                        filterOption={(input, option) =>
                            ((option?.label as string) ?? '')
                                .toLowerCase()
                                .includes(input.toLowerCase())
                        }
                        options={[
                            { value: 'toronto', label: 'Toronto' },
                            {
                                value: 'montreal',
                                label: 'Montreal',
                            },
                            {
                                value: 'vancouver',
                                label: 'Vancouver',
                            },
                        ]}
                    />
                ),
            },
            {
                label: 'Allow Clear',
                component: (
                    <Select
                        allowClear
                        placeholder="Select or clear..."
                        style={{ width: '100%' }}
                        defaultValue="option1"
                    >
                        <Option value="option1">Option 1</Option>
                        <Option value="option2">Option 2</Option>
                    </Select>
                ),
            },
            {
                label: 'Loading',
                component: (
                    <Select
                        loading
                        placeholder="Loading..."
                        style={{ width: '100%' }}
                    >
                        <Option value="option1">Option 1</Option>
                    </Select>
                ),
            },
            {
                label: 'Disabled',
                component: (
                    <Select
                        disabled
                        placeholder="Disabled select"
                        style={{ width: '100%' }}
                    >
                        <Option value="option1">Option 1</Option>
                    </Select>
                ),
            },
        ],
    };
}

export function createDatePickerVariants(): InputType {
    return {
        label: 'DatePicker',
        variants: [
            {
                label: 'Standard',
                component: (
                    <DatePicker
                        style={{ width: '100%' }}
                        placeholder="Select date"
                    />
                ),
            },
            {
                label: 'With Time',
                component: (
                    <DatePicker
                        showTime={{
                            format: 'HH:mm',
                            minuteStep: 15,
                        }}
                        format="YYYY-MM-DD HH:mm"
                        style={{ width: '100%' }}
                        placeholder="Select date and time"
                    />
                ),
            },
            {
                label: 'Disabled',
                component: (
                    <DatePicker
                        disabled
                        style={{ width: '100%' }}
                        placeholder="Disabled date picker"
                    />
                ),
            },
        ],
    };
}

export function createRangePickerVariants(): InputType {
    return {
        label: 'RangePicker',
        variants: [
            {
                label: 'Standard',
                component: <RangePicker style={{ width: '100%' }} />,
            },
            {
                label: 'With Time',
                component: (
                    <RangePicker
                        showTime={{ format: 'HH:mm' }}
                        format="YYYY-MM-DD HH:mm"
                        style={{ width: '100%' }}
                    />
                ),
            },
            {
                label: 'Disabled',
                component: <RangePicker style={{ width: '100%' }} disabled />,
            },
        ],
    };
}

export function createTimePickerVariants(): InputType {
    return {
        label: 'TimePicker',
        variants: [
            {
                label: 'Standard',
                component: (
                    <TimePicker
                        format="HH:mm"
                        style={{ width: '100%' }}
                        placeholder="Select time"
                    />
                ),
            },
            {
                label: 'Disabled',
                component: (
                    <TimePicker
                        format="HH:mm"
                        style={{ width: '100%' }}
                        placeholder="Select time"
                        disabled
                    />
                ),
            },
        ],
    };
}

export function createSwitchVariants(
    switchValue: boolean,
    setSwitchValue: (value: boolean) => void
): InputType {
    return {
        label: 'Switch',
        variants: [
            {
                label: 'Standard',
                component: (
                    <div style={{ display: 'inline-flex', width: 'auto' }}>
                        <Switch
                            checked={switchValue}
                            onChange={setSwitchValue}
                        />
                    </div>
                ),
            },
            {
                label: 'Loading',
                component: (
                    <div style={{ display: 'inline-flex', width: 'auto' }}>
                        <Switch loading />
                    </div>
                ),
            },
            {
                label: 'Disabled',
                component: (
                    <div style={{ display: 'inline-flex', width: 'auto' }}>
                        <Switch disabled />
                    </div>
                ),
            },
        ],
    };
}

export function createRadioVariants(
    radioValue: number,
    setRadioValue: (value: number) => void
): InputType {
    return {
        label: 'Radio',
        variants: [
            {
                label: 'Radio Group',
                component: (
                    <div className={style.radio_wrapper}>
                        <Radio.Group
                            value={radioValue}
                            onChange={(e) => setRadioValue(e.target.value)}
                        >
                            <Radio value={1}>Monday</Radio>
                            <Radio value={2}>Tuesday</Radio>
                            <Radio value={3}>Wednesday</Radio>
                        </Radio.Group>
                    </div>
                ),
            },
            {
                label: 'Radio Button Group',
                component: (
                    <div className={style.radio_wrapper}>
                        <Radio.Group
                            value={radioValue}
                            onChange={(e) => setRadioValue(e.target.value)}
                        >
                            <Radio.Button value={1}>Mo</Radio.Button>
                            <Radio.Button value={2}>Tu</Radio.Button>
                            <Radio.Button value={3}>We</Radio.Button>
                        </Radio.Group>
                    </div>
                ),
            },
            {
                label: 'Disabled',
                component: (
                    <div className={style.radio_wrapper}>
                        <Radio.Group disabled value={1}>
                            <Radio value={1}>Option 1</Radio>
                            <Radio value={2}>Option 2</Radio>
                            <Radio value={3}>Option 3</Radio>
                        </Radio.Group>
                    </div>
                ),
            },
        ],
    };
}

export function createSliderVariants(
    sliderValue: number,
    setSliderValue: (value: number) => void,
    rangeSliderValue: [number, number],
    setRangeSliderValue: (value: [number, number]) => void
): InputType {
    return {
        label: 'Slider',
        variants: [
            {
                label: 'Standard',
                component: (
                    <Slider
                        value={sliderValue}
                        onChange={setSliderValue}
                        min={0}
                        max={100}
                    />
                ),
            },
            {
                label: 'Range',
                component: (
                    <Slider
                        range
                        value={rangeSliderValue}
                        onChange={setRangeSliderValue}
                        min={0}
                        max={100}
                    />
                ),
            },
            {
                label: 'With Marks',
                component: (
                    <Slider
                        marks={{
                            0: '0°C',
                            25: '25°C',
                            50: '50°C',
                        }}
                        defaultValue={30}
                        min={0}
                        max={50}
                    />
                ),
            },
            {
                label: 'Disabled',
                component: (
                    <Slider defaultValue={30} min={0} max={100} disabled />
                ),
            },
        ],
    };
}
